/**
 * Haptic Feedback Utility
 * 
 * Provides native-like haptic feedback for touch interactions.
 * Uses Web Vibration API on supported devices (Android) and
 * provides visual feedback fallbacks for iOS.
 */

// Check if vibration is supported
const isVibrationSupported = () => {
  return 'vibrate' in navigator;
};

// Haptic feedback patterns (in milliseconds)
const HAPTIC_PATTERNS = {
  // Light tap - quick, subtle feedback
  light: [10],
  // Medium tap - standard button press
  medium: [15],
  // Heavy tap - strong feedback for important actions
  heavy: [25],
  // Success - double pulse for positive feedback
  success: [10, 50, 10],
  // Warning - longer pulse for warnings
  warning: [30],
  // Error - three quick pulses for errors
  error: [20, 30, 20, 30, 20],
  // Selection change - very light
  selection: [5],
  // Impact - strong single tap
  impact: [20],
};

/**
 * Trigger haptic feedback
 * @param {string} type - Type of haptic feedback (light, medium, heavy, success, warning, error, selection, impact)
 */
export const triggerHaptic = (type = 'light') => {
  const pattern = HAPTIC_PATTERNS[type] || HAPTIC_PATTERNS.light;
  
  if (isVibrationSupported()) {
    try {
      navigator.vibrate(pattern);
    } catch (error) {
      // Silently fail if vibration is blocked
      console.debug('Haptic feedback not available:', error);
    }
  }
  
  // Also dispatch a custom event for visual feedback systems
  window.dispatchEvent(new CustomEvent('haptic', { detail: { type, pattern } }));
};

/**
 * Haptic feedback presets for common interactions
 */
export const haptics = {
  // For button taps
  tap: () => triggerHaptic('light'),
  
  // For important button presses (submit, confirm)
  press: () => triggerHaptic('medium'),
  
  // For toggle switches
  toggle: () => triggerHaptic('selection'),
  
  // For successful actions
  success: () => triggerHaptic('success'),
  
  // For warnings
  warning: () => triggerHaptic('warning'),
  
  // For errors
  error: () => triggerHaptic('error'),
  
  // For navigation
  navigate: () => triggerHaptic('light'),
  
  // For card selections
  select: () => triggerHaptic('selection'),
  
  // For drag/drop
  impact: () => triggerHaptic('impact'),
};

/**
 * Create a click handler with haptic feedback
 * @param {Function} handler - Original click handler
 * @param {string} hapticType - Type of haptic feedback
 */
export const withHaptic = (handler, hapticType = 'light') => {
  return (event) => {
    triggerHaptic(hapticType);
    if (handler) {
      handler(event);
    }
  };
};

/**
 * Hook-style haptic feedback for React components
 */
export const useHaptics = () => {
  return {
    ...haptics,
    trigger: triggerHaptic,
    withHaptic,
  };
};

export default haptics;
